import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Container, Box, Typography, Grid, Skeleton, Button, Paper,
} from '@mui/material';
import { ArrowBack, LocationOn } from '@mui/icons-material';
import api from '../utils/api';
import LibraryCard from '../components/LibraryCard';
import DesktopNav from '../components/DesktopNav';
import StudentBottomNav from '../components/StudentBottomNav';

function SeoLocationPage() {
  const location = useLocation();
  const navigate = useNavigate();

  // Extract slug from URL path: /library-in-uttam_nagar → uttam_nagar
  const slug = location.pathname.replace('/library-in-', '');

  const [pageData, setPageData] = useState(null);
  const [libraries, setLibraries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (slug) fetchPageData();
  }, [slug]);

  const fetchPageData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/seo/public/${slug}`);
      setPageData(response.data.data.page);
      setLibraries(response.data.data.libraries);
    } catch (err) {
      if (err.response?.status === 404) {
        setError('Page not found');
      } else {
        setError('Failed to load page');
      }
    } finally {
      setLoading(false);
    }
  };

  // Set document meta tags
  useEffect(() => {
    if (!pageData) return;

    if (pageData.meta_title) {
      document.title = pageData.meta_title;
    }

    const setMeta = (name, content) => {
      if (!content) return;
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', pageData.meta_description);
    setMeta('keywords', pageData.meta_keywords);

    return () => {
      document.title = 'BookMyLibrary';
    };
  }, [pageData]);

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh' }}>
        <DesktopNav />
        <Container maxWidth="xl" sx={{ py: 4 }}>
          <Skeleton variant="text" width={300} height={50} />
          <Skeleton variant="text" width={500} height={30} sx={{ mb: 4 }} />
          <Grid container spacing={3}>
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <Skeleton variant="rectangular" height={350} sx={{ borderRadius: 3 }} />
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ minHeight: '100vh' }}>
        <DesktopNav />
        <Container maxWidth="md" sx={{ py: 8, textAlign: 'center' }}>
          <Typography variant="h4" fontWeight="700" gutterBottom>
            {error === 'Page not found' ? '404 - Page Not Found' : 'Error'}
          </Typography>
          <Typography variant="body1" color="text.secondary" mb={3}>
            {error === 'Page not found'
              ? 'The page you are looking for does not exist.'
              : 'Something went wrong. Please try again.'}
          </Typography>
          <Button variant="contained" onClick={() => navigate('/libraries')} sx={{ borderRadius: 2, textTransform: 'none' }}>
            Browse All Libraries
          </Button>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', pb: { xs: 10, md: 0 } }}>
      <DesktopNav />

      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #F26522 0%, #C94E10 100%)',
          color: 'white',
          py: { xs: 4, md: 6 },
        }}
      >
        <Container maxWidth="xl">
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/libraries')}
            sx={{ color: 'white', mb: 2, textTransform: 'none' }}
          >
            All Libraries
          </Button>
          <Typography variant="h3" fontWeight="800" gutterBottom sx={{ fontSize: { xs: '1.8rem', md: '2.5rem' } }}>
            Libraries in {pageData.location_name}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, opacity: 0.9 }}>
            <LocationOn />
            <Typography variant="h6" sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }}>
              {libraries.length} {libraries.length === 1 ? 'library' : 'libraries'} found in {pageData.location_name}
            </Typography>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Libraries Grid — show first 10 only */}
        {libraries.length > 0 ? (
          <>
            <Grid container spacing={3} sx={{ mb: 4 }}>
              {libraries.slice(0, 10).map((library) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={library.id}>
                  <LibraryCard library={library} />
                </Grid>
              ))}
            </Grid>
            {libraries.length > 10 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate(`/libraries?search=${encodeURIComponent(pageData.location_name)}`)}
                  sx={{
                    borderRadius: 3,
                    px: 5,
                    py: 1.5,
                    fontWeight: 600,
                    textTransform: 'none',
                    background: 'linear-gradient(135deg, #F26522 0%, #C94E10 100%)',
                  }}
                >
                  Show More Libraries in {pageData.location_name}
                </Button>
              </Box>
            )}
          </>
        ) : (
          <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 3, mb: 4 }}>
            <Typography variant="h6" fontWeight="600" gutterBottom>
              No libraries found in this area yet
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              We're expanding! Check back soon or browse all available libraries.
            </Typography>
            <Button variant="outlined" onClick={() => navigate('/libraries')} sx={{ borderRadius: 2, textTransform: 'none' }}>
              Browse All Libraries
            </Button>
          </Paper>
        )}

        {/* Page Content */}
        {pageData.page_content && (
          <Paper sx={{ p: { xs: 3, md: 4 }, borderRadius: 3 }}>
            <div dangerouslySetInnerHTML={{ __html: pageData.page_content }} />
          </Paper>
        )}
      </Container>

      <StudentBottomNav />
    </Box>
  );
}

export default SeoLocationPage;
